import {
    useCallback,
    useContext,
    useEffect,
    useRef,
} from 'react';

import { LocationContext } from 'app/containers/LocationProvider';
import { useCartMutationInBasket } from 'app/views/Basket/hooks';

import {
    useReadyToEatContradictionCheck,
} from './useReadyToEatContradictionCheck';

/**
 * @description Маршруты, на которых открыт чекаут. Возвращение на них после корзины
 * считается новой сессией чекаута: противоречие проверяется заново.
 */
const CHECKOUT_ROUTE_PREFIXES = ['/checkout', '/pay'];

const getInitialCheckState = () => ({
    isCheckoutOpened: false,
    isAddressStepOpened: false,
    isFinalScreenShown: false,
    locationKey: null,
    hasReadyToEatDishes: null,
    isNextStepButtonDisabled: null,
    isDialogRejected: false,
    isContradictionBlocking: false,
    isDialogShown: false,
});

/**
 * @description Собирает логику «готовые блюда + регион» в единый хук: провайдит
 * состояние диалога, проверку противоречия при входе в checkout, открытии шага адреса
 * и смене региона, удаление готовых блюд из корзины и управление кнопкой перехода
 * к следующему шагу.
 *
 * @param {boolean} isAddressStepOpened Шаг адреса открыт (пошаговый checkout).
 * @param {boolean} isFinalScreenShown Одностраничный checkout: пользователь видит
 * финальный экран чекаута, то есть адрес пройден и ни один шаг не открыт.
 * @param {boolean} isSingleStepCheckout Одностраничный checkout: напоминание
 * показывается один раз на финальном экране, кнопка оплаты блокируется до снятия
 * противоречия.
 */
export const useReadyToEatContradictionDefine = ({
    cart,
    dispatch,
    pathname = '',
    isAddressStepOpened = false,
    isFinalScreenShown = false,
    isSingleStepCheckout = false,
}) => {
    const { locationKey, isLocationDataLoading } = useContext(LocationContext);
    const checkStateRef = useRef(getInitialCheckState());

    const {
        isReadyToEatDialogShown,
        setIsReadyToEatDialogShown,
        hasContradiction,
        readyToEatDishes,
    } = useReadyToEatContradictionCheck(cart);

    const { mutateItemInCart } = useCartMutationInBasket();
    const hasReadyToEatDishes = readyToEatDishes.length > 0;

    const isCheckoutOpened = CHECKOUT_ROUTE_PREFIXES
        .some((routePrefix) => pathname.startsWith(routePrefix));

    /**
     * @description Кнопка следующего шага блокируется на время сессии чекаута, пока
     * противоречие не подтверждённо снято. Повторные диспатчи пропускаем, чтобы не
     * перерисовывать шаги после каждого обновления корзины.
     */
    const setNextStepButtonDisabled = useCallback((isDisabled) => {
        const checkState = checkStateRef.current;

        if (checkState.isNextStepButtonDisabled === isDisabled) return;

        checkState.isNextStepButtonDisabled = isDisabled;

        if (!dispatch) return;

        dispatch({
            type: 'SET_NEXT_STEP_BUTTON_DISABLED',
            payload: isDisabled,
        });
    }, [dispatch]);

    /**
     * @description Удаляет готовые блюда и снимает блокировку. Возвращает промис с
     * корзиной после мутации, чтобы вызывающий код мог решить, продолжать ли
     * оформление: если блюда в корзине остались, чекаут не закрываем. При ошибке
     * запроса (или если удалять нечего) резолвится в null — решение о переходе
     * принимать нельзя, блюда могли остаться в корзине.
     */
    const handleRemoveReadyToEatDishes = useCallback(() => {
        const dishIds = readyToEatDishes.map(({ dish_id: dishId }) => String(dishId));

        const removalPromise = dishIds.length
            ? Promise.resolve(mutateItemInCart({
                dishes: dishIds.map((dishId) => ({ dish_id: dishId, portions: 0 })),
            }))
                .then((result) => result?.data?.addItemsToCartByPortionsList?.cart || null)
                .catch(() => null)
            : Promise.resolve(null);

        checkStateRef.current.isDialogRejected = false;
        checkStateRef.current.isContradictionBlocking = false;
        checkStateRef.current.isDialogShown = false;
        setIsReadyToEatDialogShown(false);
        setNextStepButtonDisabled(false);

        return removalPromise;
    }, [
        readyToEatDishes,
        mutateItemInCart,
        setIsReadyToEatDialogShown,
        setNextStepButtonDisabled,
    ]);

    /**
     * @description Пользователь оставил готовые блюда в корзине: диалог больше не
     * показываем, пока пользователь не вернётся на шаг адреса, не сменит регион
     * или не откроет чекаут заново. Блокировка шага адреса при этом остаётся.
     *
     * В одностраничном checkout сбрасываем и отметку о показе, чтобы после
     * повторного пропуска адреса напоминание появилось снова на финальном экране:
     * иначе кнопка оплаты останется заблокированной без объяснения.
     */
    const handleRejectReadyToEatDialog = useCallback(() => {
        checkStateRef.current.isDialogRejected = true;
        checkStateRef.current.isDialogShown = false;
        setIsReadyToEatDialogShown(false);
    }, [setIsReadyToEatDialogShown]);

    useEffect(() => {
        const checkState = checkStateRef.current;

        if (!isCheckoutOpened) {
            checkState.isCheckoutOpened = false;
            return;
        }

        if (isLocationDataLoading) return;

        const isNewCheckoutSession = !checkState.isCheckoutOpened;
        const isAddressStepShown = isAddressStepOpened && !checkState.isAddressStepOpened;
        const isAddressStepChanged = isAddressStepOpened !== checkState.isAddressStepOpened;
        const isFinalScreenChanged = isFinalScreenShown !== checkState.isFinalScreenShown;
        const isLocationChanged = checkState.locationKey !== locationKey;
        const isReadyToEatDishesChanged = checkState.hasReadyToEatDishes !== hasReadyToEatDishes;

        const isCheckNeeded = isNewCheckoutSession
            || isAddressStepChanged
            || isFinalScreenChanged
            || isLocationChanged
            || isReadyToEatDishesChanged;

        if (!isCheckNeeded) return;

        checkState.isCheckoutOpened = true;
        checkState.isAddressStepOpened = isAddressStepOpened;
        checkState.isFinalScreenShown = isFinalScreenShown;
        checkState.locationKey = locationKey;
        checkState.hasReadyToEatDishes = hasReadyToEatDishes;

        const isCheckRestarted = isNewCheckoutSession || isAddressStepShown || isLocationChanged;

        if (!hasContradiction) {
            /**
             * Противоречие могло пропасть из корзины из-за обновления кэша Apollo
             * (например, пока едет запрос после ввода квартиры). Снимать блокировку
             * в таком случае нельзя, иначе кнопка снова разблокируется, а диалог
             * покажется повторно. Ждём новой сессии, открытия шага адреса, смены
             * региона или явного удаления блюд через handleRemoveReadyToEatDishes.
             */
            if (!isCheckRestarted) return;

            /**
             * Отметку о показе здесь сбрасывать нельзя: противоречие исчезло из
             * корзины временно, поэтому, как только блюда вернутся, напоминание
             * всплыло бы на финальном экране второй раз (пользователь уже видел
             * его в этой сессии чекаута). Сбрасывает её только явное действие
             * пользователя — отказ или удаление блюд.
             */
            checkState.isDialogRejected = false;
            checkState.isContradictionBlocking = false;
            setIsReadyToEatDialogShown(false);
            setNextStepButtonDisabled(false);
            return;
        }

        if (isCheckRestarted) {
            checkState.isDialogRejected = false;
            checkState.isContradictionBlocking = true;
        }

        /**
         * В одностраничном checkout все шаги свёрнуты на один экран, поэтому
         * напоминание показывается один раз и только на финальном экране, который
         * пользователю показывают по итогу: адрес пройден, ни один шаг не открыт.
         * Пока открыт любой шаг (адреса, оплаты и т.д.), диалог не всплывает, иначе
         * он дублируется при каждом переключении шагов. Кнопка оплаты при этом
         * остаётся заблокированной с момента обнаружения противоречия до его снятия.
         *
         * Уход с финального экрана лишь прячет поп-ап: отметка о показе при этом
         * сохраняется, иначе напоминание всплывало бы заново при каждом возврате
         * пользователя на финальный экран. Сбрасывает её только явное действие
         * пользователя — отказ или удаление блюд.
         */
        if (isSingleStepCheckout) {
            if (!checkState.isDialogShown && isFinalScreenShown) {
                checkState.isDialogShown = true;
                setIsReadyToEatDialogShown(true);
            }

            if (checkState.isDialogShown && !isFinalScreenShown) {
                /**
                 * Пользователь ушёл править шаг: убираем поп-ап, вернём его на
                 * финальном экране только если пользователь сам его отклонил.
                 */
                setIsReadyToEatDialogShown(false);
            }

            setNextStepButtonDisabled(checkState.isContradictionBlocking);
            return;
        }

        /**
         * В пошаговом checkout противоречие касается только адреса доставки, поэтому
         * диалог и блокировка живут на шаге адреса: на шаге телефона кнопка должна
         * оставаться активной, а при повторном открытии шага адреса пользователь
         * снова видит напоминание.
         */
        setIsReadyToEatDialogShown(isAddressStepOpened && !checkState.isDialogRejected);
        setNextStepButtonDisabled(isAddressStepOpened);
    }, [
        isCheckoutOpened,
        isAddressStepOpened,
        isFinalScreenShown,
        isSingleStepCheckout,
        isLocationDataLoading,
        locationKey,
        hasReadyToEatDishes,
        hasContradiction,
        setIsReadyToEatDialogShown,
        setNextStepButtonDisabled,
    ]);

    return {
        isReadyToEatDialogShown,
        handleRemoveReadyToEatDishes,
        handleRejectReadyToEatDialog,
    };
};
